import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV, PROFILE } from "@/data/content";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the mobile menu first, then scroll after it has collapsed.
  // (Scrolling while the menu collapses interrupts the smooth scroll on mobile.)
  const handleNav = (id) => {
    setOpen(false);
    setTimeout(() => scrollTo(id), 320);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-[#050505]/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="container mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <button
            onClick={() => handleNav("hero")}
            className="font-heading text-2xl tracking-tight text-white hover:text-volt transition-colors"
            data-testid="nav-logo"
          >
            KS<span className="text-volt">.</span>
          </button>

          {/* Desktop */}
          <nav className="hidden md:flex items-center gap-9">
            {NAV.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNav(item.id)}
                className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-volt transition-colors"
                data-testid={`nav-link-${item.id}`}
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => handleNav("contact")}
              className="font-heading tracking-wide text-sm bg-volt text-black px-5 py-2.5 hover:bg-white transition-colors"
              data-testid="nav-cta-work"
            >
              WORK WITH ME
            </button>
          </nav>

          {/* Mobile toggle */}
          <button
            className="md:hidden text-white"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#050505]/95 backdrop-blur-xl border-b border-white/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNav(item.id)}
                  className="font-mono text-sm uppercase tracking-[0.2em] text-neutral-300 text-left hover:text-volt"
                  data-testid={`nav-mobile-link-${item.id}`}
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => handleNav("contact")}
                className="font-heading tracking-wide bg-volt text-black px-5 py-3 text-center"
                data-testid="nav-mobile-cta"
              >
                WORK WITH ME
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
